import { cva, VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';

import { cn } from '../../libs/utils';
import { semanticColor } from '../../theme/semanticColor';
import { Svg } from '../common';

const alertVariants = cva('rounded-bzc-small  flex gap-tiny-medium items-start', {
  variants: {
    variant: {
      success: 'text-success-foreground-high bg-success-background-low',
      warning: 'text-warning-foreground-high bg-warning-background-low',
      promo: 'text-promo-foreground-high bg-promo-background-low',
      custom: ''
    },
    size: {
      small: 'p-normal-small caption-small-regular',
      medium: 'py-normal-small px-normal-medium body-small-regular'
    }
  },
  defaultVariants: {
    variant: 'success',
    size: 'medium'
  }
});

interface AlertProps extends VariantProps<typeof alertVariants> {
  className?: string;
  graphic?: ReactNode;
  children?: ReactNode;
  bg?: string;
}

const ALERT_SVG_LIST = {
  success: 'ic_check.svg',
  warning: 'ic_exclamation-mark.svg',
  promo: 'ic_gift.svg',
  custom: ''
};

const ALERT_SOURCE_COLOR_LIST = {
  success: semanticColor.success.foreground.high,
  warning: semanticColor.warning.foreground.high,
  promo: semanticColor.promo.foreground.high,
  custom: ''
};

export function Alert({ variant = 'success', className, graphic, children, bg, size, ...props }: AlertProps) {
  const renderGraphic = () => {
    const source = ALERT_SVG_LIST[variant ?? 'success'];
    const sourceColor = ALERT_SOURCE_COLOR_LIST[variant ?? 'success'];
    const graphicSize = size === 'medium' ? 18 : 16;

    return (
      <div
        className={cn('flex flex-shrink-0 items-center', {
          'h-[22px]': !graphic && size === 'medium',
          'h-[19px]': !graphic && size === 'small'
        })}
      >
        {graphic ? (
          graphic
        ) : (
          <Svg
            width={graphicSize}
            height={graphicSize}
            src={source}
            color={sourceColor}
            loader={<div className={'h-[18px] w-[18px]'} />}
            className={'flex-shrink-0'}
          />
        )}
      </div>
    );
  };

  return (
    <div
      className={alertVariants({ variant, size, className })}
      style={{
        background: bg
      }}
      role={'alert'}
      {...props}
    >
      {renderGraphic()}
      {children}
    </div>
  );
}

export const AlertTitle = ({ className, ...props }: Pick<AlertProps, 'className' | 'children'>) => {
  return <h5 className={cn(className)} {...props} />;
};
